import i18n from "src/i18n";

/**
 * The Okta SDK (@okta/okta-auth-js, okta-signin-widget) rejects with raw
 * English technical messages (AuthSdkError, OAuthError...). Those must never
 * reach the snackbar as-is: known causes map to an actionable French message,
 * anything unrecognized falls back to the generic one.
 */

// okta-auth-js validateClaims checks token lifetime against the device clock:
// a clock off by more than the allowed skew rejects freshly issued tokens with
// these messages, so retrying cannot succeed until the user fixes the clock.
const DEVICE_CLOCK_SDK_MESSAGES = [
  "The JWT expired and is no longer valid",
  "The JWT was issued in the future",
];

// Token integrity/verification failures: the received token is corrupted,
// incomplete or fails security checks (browser extension interference,
// corrupted storage...). Wording mandated by PROD-31067: point the customer
// to another browser or device.
const INVALID_TOKEN_SDK_MESSAGES = [
  "The token signature is not valid",
  "Token hash verification failed",
  "Malformed token",
  "invalid idToken",
  "invalid accessToken",
  "invalid refreshToken",
  "Only idTokens may be verified",
  "The request does not match client configuration",
];

export function extractAuthErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    if ("errorSummary" in error && typeof error.errorSummary === "string") {
      return error.errorSummary;
    }
  }
  return "";
}

export function getAuthErrorDisplayMessage(error: unknown): string {
  const technicalMessage = extractAuthErrorMessage(error);

  if (
    DEVICE_CLOCK_SDK_MESSAGES.some((sdkMessage) =>
      technicalMessage.includes(sdkMessage),
    )
  ) {
    return i18n.t(
      "Impossible de finaliser la connexion : l'horloge de votre appareil semble mal réglée. Vérifiez la date et l'heure de votre appareil, puis reconnectez-vous.",
    );
  }

  if (
    INVALID_TOKEN_SDK_MESSAGES.some((sdkMessage) =>
      technicalMessage.includes(sdkMessage),
    )
  ) {
    return i18n.t(
      "Impossible de finaliser la connexion : veuillez réessayer avec un autre navigateur ou sur un autre appareil.",
    );
  }

  return i18n.t(
    "Une erreur est survenue lors de la connexion. Veuillez réessayer.",
  );
}
